import React from 'react';
import {
  Grid,
  SvgIcon,
  Link,
  BreadcrumbsProps,
  Breadcrumbs,
} from '@mui/material';
import { ReactComponent as ArrowForwardIcon } from 'assets/arrow-forward.svg';
import { SxProps } from '@mui/system';
import { useNavigate } from 'react-router-dom';

export interface IKFBreadCrumItem {
  title: string | React.ReactNode;
  path?: string | undefined;
  sameTab?: boolean | undefined;
}

export interface IKFBreadcrumbsPropsV2 extends BreadcrumbsProps {
  breadCrumbItems: IKFBreadCrumItem[];
  id: string;
  showPrevButton?: boolean | undefined;
  prevButtonIcon?: any;
  seperatorIcon?: string | React.ReactNode | undefined;
  onClickTitle?: (
    path: string | undefined,
    sameTab?: boolean | undefined,
    navigate?: any
  ) => void;
  titleSxProps?: SxProps;
  prevButtonSxProps?: SxProps;
}

export function KFBreadCrumbV2(props: IKFBreadcrumbsPropsV2){
  const {
    breadCrumbItems,
    id,
    onClickTitle,
    showPrevButton = true,
    prevButtonIcon,
    seperatorIcon,
    titleSxProps,
    prevButtonSxProps,
    ...otherProps
  } = props;

  const navigate = useNavigate();

  const onClickItem = (
    path: string | undefined,
    sameTab?: boolean | undefined
  ) => {
    if (onClickTitle) onClickTitle(path, sameTab, navigate);
    else navigate(`/${path}`);
  };

  const handleBackNavigation = () => {
    const prevTab =
      breadCrumbItems[
        breadCrumbItems.length > 1 ? breadCrumbItems.length - 2 : 0
      ];
    /*istanbul ignore else*/
    if (prevTab?.path) {
      onClickItem(prevTab.path, prevTab.sameTab);
    }
  };

  return (
    <Breadcrumbs {...otherProps}>
      <Grid
        container
        spacing={1}
        gap={1}
        direction='row'
        id={id}
        data-testid={id}
        justifyContent={{ xs: 'left', sm: 'center' }}
        alignItems='center'
      >
        {showPrevButton && breadCrumbItems.length > 1 && (
          <Grid
            id={`${id}-navigate-icon`}
            data-testid={`${id}-navigate-icon`}
            onKeyUp={(event) => {
              /*istanbul ignore else*/
              if (event.key === 'Enter') handleBackNavigation();
            }}
          >
            <SvgIcon
              viewBox='0 0 8 14'
              sx={{
                cursor: 'pointer',
                height: ({ typography }: any) => typography.pxToRem(20),
                width: ({ typography }: any) => typography.pxToRem(8),
                position: 'relative',
                top: ({ typography }: any) => typography.pxToRem(2),
                ...prevButtonSxProps,
              }}
              component={prevButtonIcon ?? ArrowForwardIcon}
              id={'breadcrumb-prev-btn'}
              data-testid={'breadcrumb-prev-btn'}
              role='navigation'
              tabIndex={0}
              onClick={() => handleBackNavigation()}
            />
          </Grid>
        )}
        {breadCrumbItems.map((bcItem: IKFBreadCrumItem, index: number) => (
          <Grid
            sx={{ padding: 0, ...titleSxProps }}
            key={`breadcrumb-item-${bcItem.title}`}
            id={`breadcrumb-item-${bcItem.title}`}
            data-testid={`breadcrumb-item-${bcItem.title}`}
          >
            <Link
              className='kfic2-bodyText'
              key={`link-item-${bcItem.title}`}
              id={`link-item-${bcItem.title}`}
              data-testid={`link-item-${bcItem.title}`}
              sx={{
                cursor: bcItem.path ? 'pointer' : 'normal',
                marginRight:
                  index < breadCrumbItems.length - 1 ? '8px !important' : '',
              }}
              onClick={() =>
                bcItem.path && onClickItem(bcItem.path, bcItem.sameTab)
              }
              onKeyUp={(event) => {
                /*istanbul ignore else*/
                if (bcItem.path && event.key === 'Enter')
                  onClickItem(bcItem.path, bcItem.sameTab);
              }}
              underline='none'
              role='navigation'
              tabIndex={0}
              aria-labelledby={`breadcrumb-item-${index}`}
            >
              <span>{bcItem.title}</span>
            </Link>
            {index < breadCrumbItems.length - 1 && (
              <span>{seperatorIcon ?? '/'}</span>
            )}
          </Grid>
        ))}
      </Grid>
    </Breadcrumbs>
  );
}
